/**
 * Native Shoes — Product Cross-Links (color selector) runtime
 * -----------------------------------------------------------------
 * Author:   Anton Siedlecki
 * Date:     2026-06-01
 * Version:  2.4.0
 *
 * v2.4.0 (2026-09-02) — Perf: fetchColorGroups() and
 * fetchLiveSizeAvailability() no longer run sequentially (one nested
 * inside the other's .then()) — they're independent (both only need the
 * sibling handle list), so the second now starts as soon as
 * injectCurrentIfMissing() resolves instead of waiting on the first too.
 * Removes one full network round trip of latency from every PDP load,
 * warm cache or not. No change to what's fetched, no cache-prefix bump —
 * pure client-side sequencing fix, see boot()'s own comment for detail.
 *
 * v2.3.0 (2026-08-28) — Per explicit user instruction ("they should
 * never flash"), boot() now fetches live per-size availability BEFORE
 * the first render instead of after it — the v2.2.0 fix stopped an
 * unavailable-in-market sibling from staying visible, but it could
 * still flash briefly during the gap between the instant first paint
 * and the live fetch resolving. That gap is gone: nothing renders
 * until both style identity AND live availability are ready, same
 * wait-then-paint-once discipline the collection grid already used.
 * Removed the now-unused refreshLiveSizeAvailability() wrapper (its
 * post-render-refresh job doesn't exist anymore) — the fetch is now
 * inline in boot(), gating renderFromData() directly.
 *
 * v2.2.0 (2026-08-28) — Fixed isFullyOOS() treating a sibling fully
 * unpublished from the visitor's market the same as "live data not
 * loaded yet," so it kept showing as an active swatch and 404'd on
 * click (found live on Robbie Dash Kids). See isFullyOOS()'s own
 * comment for full detail.
 * Purpose:  Fetches the consolidated_style metaobject for the
 *           current product and renders sibling-color cross-links
 *           (thumbnails or swatches) under the product PDP.
 *
 *           v2.1.0 changes (2026-08-28) — Style identity cache/fetch
 *           (gql, readCache/writeCache, checkStyleVersion/
 *           revalidateInBackground, the color_swatch metaobject fetch)
 *           extracted to assets/native-swatch-shared.js, shared with
 *           assets/native-collection-swatches.js so a visit to either a
 *           collection page or this PDP for the same consolidated_style
 *           warms the cache the other reads — explicit user request,
 *           since both features were independently caching the exact
 *           same per-style data under separate namespaces. Also
 *           removed: the color_group-metadata cache/revalidation dance
 *           (colorGroupMapsEqual/patchCachedColorGroupMap/
 *           revalidateColorGroupsInBackground) — fetchColorGroups() is
 *           cheap enough (1-6 metaobject lookups, not per-sibling) to
 *           just call fresh every time identity resolves instead of
 *           caching it and needing a whole change-detection mechanism
 *           to keep that cache honest. isFullyOOS() now prefers live
 *           per-variant data (from fetchLiveSizeAvailability(), already
 *           fetched for the per-size flag) over the cached identity
 *           fetch's own `variants` field, falling back to that cached
 *           field only for this container's very first paint, before
 *           its own live fetch has resolved — see native-swatch-
 *           shared.js's header for why that field is in the shared
 *           cache at all despite the "no availability in the cache"
 *           principle used everywhere else. One accepted behavior
 *           change from the extraction: the shared module's background
 *           revalidation only refreshes the cache for the NEXT page
 *           load (matching collection grid's own pattern), unlike this
 *           file's old revalidateInBackground(), which could also
 *           correct the CURRENT render mid-view if the backend
 *           assortment changed while a shopper was on the page —
 *           consistent with how this identity data was already
 *           described as low-stakes-if-briefly-stale, just no longer
 *           self-correcting quite as eagerly for that one specific
 *           trigger. Live per-size availability, click-prevention,
 *           ?size= carry-over, and all rendering are unchanged. See
 *           native-swatch-shared.js's own header for the full design
 *           and the superset-shape rule that makes sharing safe.
 *
 *           v2.0.0 changes (2026-08-27) — RESTORED from the pre-GraphQL-
 *           removal backup (~/Downloads/native-swatch-graphql-removal-
 *           BACKUP-2026-08-25/), reversing the 2026-08-25 rewrite that
 *           moved this feature to pure inline Liquid. Root cause: that
 *           Liquid rewrite hit a genuine, confirmed-live Shopify Liquid
 *           per-render object-dereference ceiling (~24-25 distinct
 *           metaobject/all_products[] lookups per page render) — proven
 *           via a minimal isolated test snippet that still returned
 *           blank output for late-order siblings even with the absolute
 *           least possible work per call. On a 25-member style that's
 *           6 siblings silently missing their swatch image every time,
 *           with no error and no way to raise the ceiling from inside
 *           Liquid. GraphQL has no equivalent limit — it resolves
 *           server-side and returns one flat payload regardless of
 *           sibling count. Explicit user decision: bring GraphQL back
 *           for the PDP specifically (low risk here — one call per page
 *           view, no facet-refetch multiplier like the collection grid
 *           has), keep the collection grid on its current pure-Liquid
 *           architecture (already a bounded subset of swatches, not the
 *           full sibling list, so the ceiling doesn't apply there).
 *           Kept 1:1 from the restored version: all caching/
 *           invalidation logic (version-validated stale-while-
 *           revalidate, handlesHash for sibling handle renames, the
 *           separate lightweight color_group revalidation, storage
 *           hygiene), the live never-cached per-size availability path,
 *           the ?size= carry-over click handler, and every historical
 *           bug fix baked into this file over its 15 prior versions
 *           (case-insensitive handle lookups, empty-result cache-write
 *           guard, onlineStoreUrl market-filter revert, etc.) — see the
 *           full changelog below this notice for all of that history.
 *           Changed from the restored version:
 *             - Storage: sessionStorage → localStorage. The user
 *               explicitly asked for a day-long cache; sessionStorage
 *               is cleared on tab close, which would silently cap real-
 *               world cache lifetime well under a day for most
 *               shoppers. localStorage persists across tabs/sessions,
 *               which is what "cached for a day" actually requires —
 *               the existing TTL/version-check logic still expires and
 *               revalidates it correctly regardless of which storage
 *               backs it.
 *             - CACHE_TTL_MS: 15 minutes → 24 hours, per explicit
 *               request. The version-validated stale-while-revalidate
 *               probe (checkStyleVersion/revalidateInBackground) still
 *               runs on every cache hit regardless of TTL, so a real
 *               assortment change still surfaces on the next page load
 *               that touches the style — the longer TTL only affects
 *               how long a cache entry survives with NO changes to
 *               revalidate against.
 *             - Ported forward one real UX improvement made during the
 *               Liquid era that predates this restore and is worth
 *               keeping: clicking the current color's own swatch/
 *               thumbnail no longer reloads the page (it already points
 *               at the exact page the shopper is on). Implemented via a
 *               data-current-color-link marker added to the current
 *               item's own <a> in renderThumbnail()/renderSwatches(),
 *               plus a click listener that prevents default on it and a
 *               guard so the ?size= carry-over listener skips it too.
 *
 *           v1.15.0 changes (2026-08-20):
 *             - Cache revalidation now also catches a sibling's own
 *               HANDLE changing, not just edits to the consolidated_style
 *               metaobject itself. checkStyleVersion()'s probe now also
 *               fetches the sibling handle set (still far cheaper than a
 *               full fetchPage() — no images/metafields/variants) and
 *               revalidateInBackground() bypasses the cache if EITHER
 *               updatedAt or the handle set changed. Root cause: a
 *               product's handle lives on the Product, not the
 *               consolidated_style metaobject, so renaming a product
 *               (e.g. the 225-handle SFCC data-cleanup batch, see
 *               SFCC_TO_SHOPIFY_DATA_MIGRATION.md) never bumped the value
 *               this cache's only invalidation check was watching — a
 *               cached render kept linking to the old, now-redirected
 *               handle, and visibly "doubled up" a color once a later
 *               fresh render picked up the rename alongside it. Caught
 *               live by the user during that exact migration.
 *
 *           v1.14.1 changes (2026-08-19) — BUG FIX for v1.14.0:
 *             - shoe.sizes is actually a `list.single_line_text_field`
 *               metafield, NOT a list.metaobject_reference as v1.14.0
 *               assumed — confirmed directly against the live Storefront
 *               API. Its .value is a JSON array of plain strings (e.g.
 *               '["big-kids-j1"]'); the .references() sub-selection
 *               v1.14.0 used doesn't exist for this field type and
 *               silently resolved to null (no GraphQL error), so
 *               isSizeUnavailable() always saw zero size handles for
 *               EVERY sibling — including the current product itself —
 *               and flagged every color as unavailable the moment any
 *               size was selected, regardless of real stock. Reported:
 *               "Oasis Blue has full inventory but shows striked out."
 *               Fixed by reading `.value` directly (parseSizeHandlesValue())
 *               instead of `.references()`.
 *             - Also moved per-size availability OUT of the cached
 *               fetchPage() query entirely, per explicit instruction not
 *               to let this ride on the same 15-minute sessionStorage
 *               cache as the color/swatch assortment data. New
 *               fetchLiveSizeAvailability() issues a separate, never-
 *               cached GraphQL call (one product(handle:) alias per
 *               sibling) every page load, kept only in the in-memory
 *               liveSizeDataByHandle closure var — never read from or
 *               written to sessionStorage. fetchPage()'s query and
 *               CACHE_PREFIX both reverted to their pre-v1.14.0 shape
 *               (no schema change to the cached blob at all now).
 *
 *           v1.14.0 changes (2026-08-19) — see v1.14.1 above, this
 *           version had a real bug and should not be deployed on its own:
 *             - Sibling color swatches now show faded + diagonally
 *               slashed (not hidden) when the shopper's CURRENTLY
 *               SELECTED size isn't available in that color — a
 *               narrower, additive check on top of the existing
 *               isFullyOOS()/filterProducts() "hide entirely if OOS in
 *               every size" behavior. isSizeUnavailable() compares
 *               against the live selection read from
 *               document.querySelector('native-size-selector').
 *               selectedHandles (same read-path v1.11.0 already uses
 *               for the ?size= carry-over). native-size-selector.js's
 *               renderSelection() now dispatches a bubbled
 *               native:size-selection-changed CustomEvent on every
 *               selection change (select/switch/clear); this file
 *               listens on document and re-renders from the
 *               already-fetched data (lastRenderData) — no refetch, no
 *               cache write — so the flag updates live on the same
 *               page, not just on next load.
 *               Out of scope: the collection grid (native-collection-
 *               swatches.js) has no analogous "currently selected size"
 *               concept to key off, so this is PDP-only.
 *
 *           v1.13.0 changes (2026-08-11):
 *             - Cache invalidation now also accounts for color_group
 *               metadata changes (sort_order edited, group renamed, or a
 *               group handle that previously had no matching metaobject
 *               now resolving), which the existing consolidated_style
 *               updatedAt probe never caught — see v1.12.0's note below.
 *               revalidateColorGroupsInBackground() re-fetches ONLY
 *               color_group metadata for the handles already present in
 *               the cached products, and patches colorGroupMap in place
 *               (via patchCachedColorGroupMap()) if it changed — products
 *               and swatchMap are untouched, so this never forces a
 *               swatch refetch. Does not cover a product's own
 *               colorGroupMetafield being reassigned to a different
 *               group — that's a sibling-product field edit, deferred to
 *               the existing TTL cycle like any other sibling field edit.
 *
 *           v1.12.0 changes (2026-08-11):
 *             - Color group sections now render in the order defined by
 *               the color_group metaobject's `sort_order` field instead
 *               of API-response order (effectively arbitrary/insertion
 *               order). fetchColorGroups() now also requests sort_order;
 *               groupProducts() sorts the grouped result by it (parsed as
 *               a number, missing/non-numeric values sort last, ties keep
 *               their prior relative order). Note: a color_group's own
 *               sort_order edit doesn't bump the parent consolidated_style
 *               metaobject's updatedAt, so an already-cached cache entry
 *               won't pick up a sort_order change until it expires OR the
 *               separate color_group revalidation below catches it.
 *
 *           v1.11.0 changes (2026-08-10):
 *             - Carry the shopper's selected size across a color change.
 *               A delegated click listener on the container reads the
 *               current page's selected size from native-size-selector
 *               (document.querySelector('native-size-selector').
 *               selectedHandles[0]) and appends it as a ?size= query
 *               param onto whichever sibling color link was clicked.
 *               native-size-selector.js v1.3.0 reads that param on the
 *               destination page and auto-selects the equivalent size if
 *               it exists there.
 *
 *           v1.10.1 changes (2026-08-07):
 *             - REVERT of v1.10.0's onlineStoreUrl filter. Broke swatches
 *               and consolidated-style rendering broadly across
 *               collections and products almost immediately after deploy.
 *               Confirmed via a direct Storefront API query that
 *               onlineStoreUrl is null for every product this shop's
 *               Storefront API token can see — published or not, in stock
 *               or not. It reflects classic "Online Store" sales channel
 *               publication, which this token (a custom/headless
 *               Storefront API app, not the Online Store channel itself)
 *               never gets a non-null value for. The v1.10.0 filter
 *               treated every sibling as unpublished and hid nearly
 *               everything. isUnpublishedForMarket() removed;
 *               filterProducts() is back to isFullyOOS() only. See
 *               NATIVE_THEME_NOTES.md for the follow-up investigation
 *               into a real signal for this.
 *
 *           v1.10.0 changes (2026-08-07) — REVERTED, see v1.10.1:
 *             - Filter out market-unpublished siblings. Our region-publish
 *               Flow unpublishes a Style Color product from a market's
 *               Online Store the moment it goes fully out of stock there.
 *               onlineStoreUrl already resolves to null for a product not
 *               published to the Online Store in the current @inContext
 *               market, and we already fetch that field — so
 *               filterProducts() now also hides any sibling with a null
 *               onlineStoreUrl, no extra network call required. Closes a
 *               gap isFullyOOS() alone can miss: a variant with "continue
 *               selling when out of stock" enabled still reports
 *               availableForSale: true at zero quantity.
 *
 *           v1.9.0 changes (2026-08-07):
 *             - Version-validated cache. The cache previously trusted
 *               anything within its TTL with no check against the
 *               metaobject's actual current state, so a backend change
 *               to a Consolidated Style's color assortment (add/remove/
 *               reorder) could take up to a full TTL cycle to appear. Any
 *               edit to a metaobject's fields always bumps its updatedAt,
 *               so on every cache hit we now fire one lightweight
 *               background probe — metaobject { updatedAt } only, no
 *               products/variants/swatches — and compare it to the
 *               updatedAt stored with the cache entry. Mismatch bypasses
 *               the cache, runs a full refetch, overwrites the entry, and
 *               re-renders with the corrected data. A failed probe fails
 *               open (keeps the existing cache) rather than forcing a
 *               refetch — the opposite of what the v1.6.0 version-check
 *               removal was fixing, so this doesn't reintroduce that
 *               failure mode.
 *             - fetchAndRender() split into fetchFreshData() (fetch + cache
 *               write, returns data) and a thin render wrapper, so the
 *               revalidation path can reuse the same fetch/cache logic.
 *             - writeCache now deletes any other entries under the same
 *               style/country prefix before writing, so there is only ever
 *               one live row per style at a time. readCache
 *               opportunistically deletes any expired/malformed entry it
 *               encounters during its scan instead of leaving it orphaned.
 *
 *           v1.8.0 changes (2026-08-07):
 *             - ROOT CAUSE FIX: this file and native-collection-swatches.js
 *               both wrote cache entries under the identical key scheme
 *               (native-cross-links:domain:styleHandle:country:version),
 *               and reads are a version-agnostic prefix scan by design.
 *               Whichever script populated a style's cache slot first
 *               "won" for the full TTL. native-collection-swatches.js's
 *               query never requests colorGroupMetafield, so a PDP load
 *               that hit a collection-page-authored cache entry found
 *               every product's color group undefined and rendered
 *               everything ungrouped — the direct cause of "sometimes
 *               it's in a color group, sometimes it's not" with no
 *               underlying data change. CACHE_PREFIX is now unique to
 *               this file so the two features can never read each
 *               other's entries.
 *
 *           Cache behavior:
 *             - Keyed by: shop domain + style handle + country code
 *               + cache version.
 *             - Cache version = consolidated_style updatedAt + "|" +
 *               total product count. Derived from the products fetch
 *               itself — no separate version-check round-trip.
 *             - Cache is read via prefix scan (ignores version on
 *               read, uses version only as part of the write key)
 *               so a version mismatch never causes a spurious miss.
 *             - Each consolidated style handle gets its own cache
 *               entry so browsing across product groups never
 *               cross-contaminates.
 *             - 24-hour TTL as a safety fallback (v2.0.0 — was 15
 *               minutes; the version-validated probe on every cache hit
 *               is what actually keeps this fresh, the TTL is just the
 *               backstop for when that probe itself can't run).
 *
 *           v1.7.0 changes (2026-08-04):
 *             - ROOT CAUSE FIX: Shopify normalizes metaobject handles
 *               to lowercase on save (e.g. "f0000156lc"), but the
 *               erp.consolidated_style metafield reference on the
 *               product can echo back a different case (e.g.
 *               "F0000156LC") depending on how the reference was
 *               originally set. The Storefront API metaobject(handle:)
 *               lookup is an exact string match, so any case mismatch
 *               caused metaobject to resolve to null, field to be
 *               null, and fetchAllProducts() to return an empty
 *               products array. That empty array was then written to
 *               cache as a permanent-for-the-TTL entry, so every
 *               subsequent PDP load for that style rendered only the
 *               single injected "current product" swatch — even though
 *               the metaobject's style_colors list was fully populated
 *               in the admin.
 *             - STYLE_HANDLE is now lowercased immediately on read
 *               from the dataset, before it is used in the GraphQL
 *               query, the cache read prefix, and the cache write
 *               key. This guarantees the lookup always matches
 *               Shopify's normalized handle regardless of the casing
 *               stored on the product metafield.
 *             - fetchAllProducts() now logs an explicit error banner
 *               into the container (in addition to console.error) if
 *               metaobject/field resolves to null, so a bad handle is
 *               immediately visible on the PDP instead of silently
 *               rendering a single swatch.
 *             - fetchAndRender() no longer writes an empty products
 *               array to cache. If products.length is 0 after a full
 *               fetch, the cache write is skipped entirely so the
 *               next page load retries the Storefront API instead of
 *               replaying a stale empty result for the full TTL.
 *
 *           v1.6.0 changes (2026-06-23):
 *             - Fixed intermittent missing color groups on stale cache.
 *               Root cause: fetchCacheVersion() was a separate blocking
 *               API call. On failure it returned 'no-version', causing
 *               a cache miss and full re-fetch. On re-fetch,
 *               colorGroupMetafield could be null on product nodes,
 *               leaving groupHandles empty and groupProducts() putting
 *               everything in the ungrouped bucket — so group labels
 *               never appeared. Also, version key mismatches between
 *               write and read caused spurious misses.
 *             - fetchCacheVersion() removed entirely.
 *             - Cache is now read via prefix scan (matches collection
 *               swatches pattern from native-collection-swatches.js).
 *             - cacheVersion is derived from the products fetch
 *               (updatedAt + total count) — same data, one less
 *               round-trip, no gating failure modes.
 *             - getCacheKey / getCachedCrossLinks / setCachedCrossLinks
 *               replaced with readCache / writeCache.
 *             - fetchAllProducts() now returns { products, cacheVersion }
 *               so fetchAndRender() owns the version naturally.
 *
 *           v1.5.0 changes (2026-06-23):
 *             - Market-level availability fix. fetchCacheVersion() and
 *               fetchPage() queries now use @inContext(country: $country)
 *               so variant.availableForSale reflects per-market
 *               availability instead of global inventory.
 *             - gql() updated to accept and forward a variables object.
 *             - Removed CURRENT_PRODUCT_AVAILABLE / data-current-product-available.
 *             - COUNTRY_CODE fallback changed from 'default' to 'ZZ'.
 *
 *           v1.4.0 changes (2026-06-01):
 *             - If the current product is not present in the
 *               consolidated_style metaobject at all (e.g. an OOS
 *               product that was removed from the list), it is
 *               injected into the front of the swatch rail using
 *               data passed directly from Liquid via data-attributes.
 *             - New required Liquid data-attributes on container:
 *                 data-current-product-handle
 *                 data-current-product-image
 *                 data-current-product-image-alt
 *                 data-current-product-color
 *
 * Usage:    Auto-initializes for every [data-native-cross-links]
 *           element on DOMContentLoaded.
 *           Required data-attributes:
 *             data-storefront-token
 *             data-shop-domain
 *             data-style-handle
 *             data-current-path
 *             data-link-type   ("thumbnail" | swatch list style)
 *             data-current-product-handle
 *             data-current-product-image
 *             data-current-product-image-alt
 *             data-current-product-color
 */




(function () {
  'use strict';



  function debugEnabled() {
    try {
      if (new URLSearchParams(window.location.search).get('native_cross_links_debug') === '1') return true;
      if (window.localStorage && window.localStorage.getItem('native_cross_links_debug') === '1') return true;
    } catch (e) {}
    return false;
  }



  var DEBUG = debugEnabled();

  function log() {
    if (!DEBUG) return;
    console.log.apply(console, ['[cross-links]'].concat(Array.prototype.slice.call(arguments)));
  }



  function err() {
    console.error.apply(console, ['[cross-links]'].concat(Array.prototype.slice.call(arguments)));
  }



  function normalizePath(path) {
    if (!path) return '';
    return path.split('?')[0].split('#')[0].replace(/\/+$/, '').toLowerCase();
  }



  // 2026-10-03: these thumbnail/dot images were set via raw GraphQL
  // `image.url` with no CDN transform at all — same bug class fixed in
  // native-collection-swatches.js's buildDot(). width/height HTML
  // attributes constrained the DISPLAYED size, but the browser still
  // downloaded the full master file every time. Same fix here: strip any
  // existing Shopify size suffix/params, then request a real
  // width/height/crop=center transform.
  var SIZE_SUFFIX_RE = /_\d+x(\d+)?\./;

  function buildSizedUrl(url, size) {
    if (!url) return url;
    var clean;
    try {
      var u = new URL(url, window.location.href);
      u.pathname = u.pathname.replace(SIZE_SUFFIX_RE, '.');
      u.searchParams.delete('width');
      u.searchParams.delete('height');
      u.searchParams.delete('crop');
      clean = u.origin + u.pathname + (u.search ? u.search : '');
    } catch (e) {
      clean = url.split('?')[0].replace(SIZE_SUFFIX_RE, '.');
    }
    var sep = clean.indexOf('?') === -1 ? '?' : '&';
    return clean + sep + 'width=' + size + '&height=' + size + '&crop=center';
  }



  // Takes the sibling's LIVE per-variant data (from
  // fetchLiveSizeAvailability(), fetched in boot() BEFORE the first
  // render — see boot's own 2026-08-28 comment) as the primary source of
  // truth, not the cached identity fetch's own `variants` field. That
  // cached field is now only a defensive fallback for a handle that
  // somehow wasn't included in the live fetch; in the normal boot path
  // every rendered sibling already has real live data by the time this
  // runs, so `liveVariantsForThisProduct` is never actually undefined in
  // practice.
  //
  // Fixed 2026-08-28 (real bug, found live on Robbie Dash Kids/Robbie
  // Dash Print Kids): a sibling completely unpublished from the
  // visitor's market — `product(handle:)` under @inContext resolves to
  // null, so fetchLiveSizeAvailability() correctly maps it to an EMPTY
  // array, not a missing key — was being treated identically to "live
  // data hasn't loaded yet" (both were falsy on the old
  // `liveVariantsForThisProduct && liveVariantsForThisProduct.length`
  // check), so it fell through to the cached, market-blind `variants`
  // field and still showed as an active, clickable swatch that then
  // 404'd on click. `onlineStoreUrl` can't substitute for this signal on
  // this store (always null regardless of real publish state — see the
  // v1.10.0/v1.10.1 history in this file's changelog); an empty-but-
  // present live array is the real, reliable one — it means "confirmed
  // unavailable in this market," not "no data yet."
  function isFullyOOS(p, liveVariantsForThisProduct) {
    if (liveVariantsForThisProduct !== undefined) {
      return liveVariantsForThisProduct.length === 0 ||
        liveVariantsForThisProduct.every(function (v) { return !v.availableForSale; });
    }
    var variants = p.variants && p.variants.nodes;
    if (!variants || !variants.length) return false;
    return variants.every(function (v) { return !v.availableForSale; });
  }



  // Matches NativeSizeSelector.norm() in native-size-selector.js exactly —
  // shoe_sizes metaobject handles must normalize identically on both sides
  // of this comparison (the current product's selected handles vs. a
  // sibling's per-variant handles) or a real match will be missed.
  function normSizeHandle(h) {
    return String(h || '').trim().toLowerCase().replace(/\s+/g, '-');
  }



  // shoe.sizes is a `list.single_line_text_field` metafield (confirmed
  // directly against the live Storefront API 2026-08-19 — NOT a
  // metaobject_reference, despite the values looking like metaobject
  // handles) — its raw .value is a JSON-encoded array of plain strings,
  // e.g. '["big-kids-j1"]'. No .references() sub-selection exists for
  // this field type; querying it returns null, silently, rather than an
  // error (see the v1.14.1 changelog note above for how that manifested).
  function parseSizeHandlesValue(raw) {
    if (!raw) return [];
    var parsed;
    try { parsed = JSON.parse(raw); } catch (e) { return []; }
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normSizeHandle);
  }



  // 2026-08-19: per-size-per-color availability, layered on top of (not a
  // replacement for) isFullyOOS(). A sibling that's fully OOS is removed
  // entirely by filterProducts() — this instead flags a sibling that's
  // still in stock overall but doesn't carry the shopper's CURRENTLY
  // SELECTED size, so it still renders (as a real color option) but
  // visually faded/slashed rather than hidden. No selection ⇒ never
  // flagged. `liveVariants` comes from fetchLiveSizeAvailability() below —
  // deliberately NOT the cached product/variant data from fetchPage(),
  // per explicit instruction not to let a stale cache drive availability.
  // Missing/not-yet-loaded live data fails open (no false slash while the
  // live fetch is still in flight).
  function isSizeUnavailable(liveVariants, selectedSizeHandlesNorm) {
    if (!selectedSizeHandlesNorm || !selectedSizeHandlesNorm.length) return false;
    if (!liveVariants || !liveVariants.length) return false;
    return !liveVariants.some(function (v) {
      if (!v.availableForSale) return false;
      return v.sizeHandlesNorm.some(function (h) { return selectedSizeHandlesNorm.indexOf(h) !== -1; });
    });
  }



  // Reads the live selection from native-size-selector rather than a
  // cached/persisted value, so a same-page size click is reflected the
  // next time this is called (see the native:size-selection-changed
  // listener in initContainer() below, which triggers exactly that).
  // Feature 323: inside the free shipping gap product's quick view (a quick-add-modal with
  // data-keep-cross-links) the swatches belong to a different product (dekkos, no sizes), so the
  // size picked on the page behind it must not slash them as "not available in your size".
  function inGapQuickView(container) {
    return !!(container && container.closest && container.closest('quick-add-modal[data-keep-cross-links]'));
  }

  function getSelectedSizeHandlesNorm(container) {
    if (inGapQuickView(container)) return [];
    var el = document.querySelector('native-size-selector');
    var handles = (el && el.selectedHandles) || [];
    return handles.map(normSizeHandle);
  }



  // REVERTED in 1.10.1 (2026-08-07) — see file changelog. onlineStoreUrl was
  // confirmed null for every product this store's Storefront API token can
  // see, published or not, so this filtered out nearly everything. Do not
  // reintroduce without a verified alternative signal.



  function initContainer(CONTAINER) {
    var STOREFRONT_TOKEN          = CONTAINER.dataset.storefrontToken || '';
    var SHOP_DOMAIN               = CONTAINER.dataset.shopDomain || '';

    // FIX (v1.7.0): Shopify normalizes metaobject handles to lowercase
    // on save. The erp.consolidated_style metafield reference can echo
    // back a different case than what's actually stored on the
    // metaobject, and metaobject(handle:) lookups are exact-match.
    // Lowercasing here guarantees the GraphQL lookup, the cache read
    // prefix, and the cache write key all agree with Shopify's
    // normalized handle — regardless of what case the metafield holds.
    var STYLE_HANDLE_RAW          = CONTAINER.dataset.styleHandle || '';
    var STYLE_HANDLE              = STYLE_HANDLE_RAW.toLowerCase();

    var CURRENT_PATH              = normalizePath(CONTAINER.dataset.currentPath || '');
    var LINK_TYPE                 = CONTAINER.dataset.linkType || '';


    var CURRENT_PRODUCT_HANDLE    = CONTAINER.dataset.currentProductHandle || '';
    var CURRENT_PRODUCT_IMAGE     = CONTAINER.dataset.currentProductImage || '';
    var CURRENT_PRODUCT_IMAGE_ALT = CONTAINER.dataset.currentProductImageAlt || '';
    var CURRENT_PRODUCT_COLOR     = CONTAINER.dataset.currentProductColor || '';


    // v2.0.0: 24 hours (was 15 minutes) — see the file-level changelog
    // entry above for why. localStorage (was sessionStorage) so the
    // cache actually survives across tabs/sessions for a full day
    // instead of being capped at whatever a single tab's lifetime is.
    var CACHE_TTL_MS = 24 * 60 * 60 * 1000;
    var CACHE_PREFIX = 'native-product-cross-links';
    var COUNTRY_CODE = (window.NativeShops && window.NativeShops.config && window.NativeShops.config.countryCode) || 'ZZ';



    log('token:', STOREFRONT_TOKEN ? '✓ present' : '✗ MISSING');
    log('domain:', SHOP_DOMAIN);
    log('style handle (raw from Liquid):', STYLE_HANDLE_RAW);
    log('style handle (normalized, used for lookup):', STYLE_HANDLE);
    log('current path (normalized):', CURRENT_PATH);
    log('current product handle:', CURRENT_PRODUCT_HANDLE);
    log('country code:', COUNTRY_CODE);
    log('link type:', LINK_TYPE);



    // 2026-08-25 (ported forward through the v2.0.0 restore): the current
    // color's own swatch/thumbnail link (marked with data-current-color-
    // link by renderThumbnail()/renderSwatches() below) always points at
    // the exact page the shopper is already on — clicking it would only
    // trigger a wasted full page reload of identical content. Stop it
    // before the ?size= carry-over listener below even runs, since
    // there's nothing useful to carry over to the same page anyway.
    CONTAINER.addEventListener('click', function (e) {
      var link = e.target.closest('[data-current-color-link]');
      if (!link || !CONTAINER.contains(link)) return;
      e.preventDefault();
    });



    // v1.11.0 (2026-08-10): carry the shopper's selected size across a
    // color change. Each color is a separate product here, so clicking a
    // sibling color link is a full page navigation — read the current
    // page's selected size from native-size-selector and append it as a
    // ?size= query param on the destination link. native-size-selector.js
    // reads that param on load and auto-selects the equivalent size.
    // Delegated on the container (not per-link) since links are rebuilt
    // whenever fetchAndRender()/cache data changes. Skips the current
    // color's own link (data-current-color-link) — see the listener
    // just above, there's nothing to carry a size param to on the same
    // page.
    CONTAINER.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link || !CONTAINER.contains(link)) return;
      if (link.hasAttribute('data-current-color-link')) return;
      if (inGapQuickView(CONTAINER)) return;

      var sizeSelector = document.querySelector('native-size-selector');
      var currentSize = sizeSelector && sizeSelector.selectedHandles && sizeSelector.selectedHandles[0];
      if (!currentSize) return;

      try {
        var url = new URL(link.href, window.location.href);
        url.searchParams.set('size', currentSize);
        link.href = url.toString();
      } catch (e2) {
        log('failed to append size param to color link', e2);
      }
    });



    function finalize() {
      CONTAINER.classList.remove('native-cross-links--loading');
      CONTAINER.removeAttribute('aria-busy');
    }




    // ─── Style identity cache/fetch (gql, readCache/writeCache,
    // checkStyleVersion/revalidateInBackground) moved to assets/native-
    // swatch-shared.js (2026-08-28) — shared with assets/native-
    // collection-swatches.js so a visit to either page type warms the
    // cache the other reads. Called below as window.NativeShops.
    // swatchShared.getStyleIdentity()/fetchSwatches()/gql(). One real
    // behavior change from the extraction: the shared module's
    // background revalidation only refreshes the cache for the NEXT
    // page load, unlike this file's old revalidateInBackground(), which
    // could also correct the CURRENT render mid-view. That's consistent
    // with how this same identity data was already described as
    // "changes rarely, isn't safety-critical if briefly stale" — see
    // that file's header for the full reasoning.
    //
    // The separate color_group-metadata revalidation dance this file
    // used to do (colorGroupMapsEqual/patchCachedColorGroupMap/
    // revalidateColorGroupsInBackground, patching a persisted
    // colorGroupMap into place if sort_order/name changed) is also
    // gone — colorGroupMap isn't part of the shared identity concept at
    // all (collection grid never needs it), and fetchColorGroups()
    // below is cheap enough (one metaobject lookup per DISTINCT group,
    // typically 1-6 total) to just call fresh every time identity
    // resolves, rather than caching it and needing a whole change-
    // detection mechanism to keep that cache honest.
    // ─────────────────────────────────────────────────────────────────




    if (!STOREFRONT_TOKEN) {
      err('storefront token missing');
      CONTAINER.innerHTML = '<li style="color:red;font-size:11px;">[cross-links] storefront token missing</li>';
      finalize();
      return;
    }



    // Standalone product — no consolidated_style at all. Rather than
    // hiding the swatch UI entirely (the old behavior), render a single
    // self-swatch for this product's own color, mirroring what
    // native-collection-swatches.js's renderSelfOnly() already does on
    // the collection grid for the same case. No style_colors to fetch,
    // so skip getStyleIdentity entirely and go straight to
    // injectCurrentIfMissing([], {}), which builds exactly this
    // synthetic single-product entry from the container's own
    // data-current-* attributes and fetches its own color_swatch.
    if (!STYLE_HANDLE) {
      log('consolidated_style handle is blank — rendering self-only swatch (standalone product)');
      var selfSwatchMap = {};
      injectCurrentIfMissing([], selfSwatchMap).then(function (products) {
        var handles = products.map(function (p) { return p.handle; }).filter(Boolean);
        return fetchLiveSizeAvailability(handles).then(function (map) {
          liveSizeDataByHandle = map;
          renderFromData({ products: products, swatchMap: selfSwatchMap, colorGroupMap: {} });
        });
      }).catch(function (e) {
        err('self-only render error:', e);
        CONTAINER.innerHTML = '';
        finalize();
      });
      return;
    }



    // gql(), the full paginated style_colors fetch, and the color_swatch
    // metaobject fetch all moved to assets/native-swatch-shared.js — see
    // the bridge comment above and that file's own header. This file
    // keeps only fetchColorGroups() (color_group METAOBJECT metadata —
    // display name/sort order — a genuinely PDP-only need collection
    // grid never has, so it isn't part of the shared identity shape).
    function fetchColorGroups(groupHandles) {
      if (!groupHandles.length) return Promise.resolve({});


      var fields = groupHandles.map(function (h, i) {
        // FIX (v1.7.0): lowercase group handles, same reasoning as above.
        var hNorm = (h || '').toLowerCase();
        return 'g' + i + ': metaobject(handle: { handle: "' + hNorm + '", type: "color_group" }) {' +
          ' group_name: field(key: "color_group_name") { value }' +
          ' sort_order: field(key: "sort_order") { value }' +
          ' }';
      }).join(' ');


      return window.NativeShops.swatchShared.gql(SHOP_DOMAIN, STOREFRONT_TOKEN, '{ ' + fields + ' }').then(function (data) {
        var map = {};
        groupHandles.forEach(function (h, i) { map[h] = data && data.data && data.data['g' + i]; });
        return map;
      });
    }



    // ─── Live per-size availability (2026-08-19) ───────────────────────────
    // Deliberately NEVER written to or read from the localStorage cache
    // above — user explicitly asked for this to stay dynamic, not baked
    // into the same day-long-TTL blob as the color/swatch assortment data.
    // Fetched once per page load (see the two call sites at boot, below)
    // and kept only in the in-memory liveSizeDataByHandle closure var;
    // re-render on a same-page size change (native:size-selection-changed)
    // reuses this same fetch rather than re-hitting the network — nothing
    // about availability changes just because the shopper clicked a size.
    function fetchLiveSizeAvailability(handles) {
      if (!handles || !handles.length) return Promise.resolve({});

      var fields = handles.map(function (h, i) {
        return 'p' + i + ': product(handle: "' + h.replace(/"/g, '\\"') + '") {' +
          ' variants(first: 100) {' +
          '   nodes {' +
          '     availableForSale' +
          '     sizeHandles: metafield(namespace: "shoe", key: "sizes") { value }' +
          '   }' +
          ' }' +
          '}';
      }).join(' ');

      return window.NativeShops.swatchShared.gql(
        SHOP_DOMAIN, STOREFRONT_TOKEN,
        'query NativeCrossLinksLiveSizeAvailability($country: CountryCode!)' +
        ' @inContext(country: $country) { ' + fields + ' }',
        { country: COUNTRY_CODE }
      ).then(function (data) {
        var map = {};
        handles.forEach(function (h, i) {
          var product  = data && data.data && data.data['p' + i];
          var variants = (product && product.variants && product.variants.nodes) || [];
          map[h] = variants.map(function (v) {
            return {
              availableForSale: !!v.availableForSale,
              sizeHandlesNorm: parseSizeHandlesValue(v.sizeHandles && v.sizeHandles.value)
            };
          });
        });
        return map;
      }).catch(function (e) {
        log('live size availability fetch failed — failing open (no slashes)', e);
        return {};
      });
    }



    function groupProducts(products, colorGroupMap) {
      var groupOrder = [];
      var groups     = {};
      var ungrouped  = [];


      products.forEach(function (p) {
        var groupHandle = p.colorGroupMetafield && p.colorGroupMetafield.value;
        if (groupHandle) {
          if (!groups[groupHandle]) { groups[groupHandle] = []; groupOrder.push(groupHandle); }
          groups[groupHandle].push(p);
        } else {
          ungrouped.push(p);
        }
      });


      var result = groupOrder.map(function (h) {
        var meta = colorGroupMap[h];
        var name = (meta && meta.group_name && meta.group_name.value) || h;
        var rawSortOrder = meta && meta.sort_order && meta.sort_order.value;
        var sortOrder = parseFloat(rawSortOrder);
        if (isNaN(sortOrder)) sortOrder = Infinity;
        return { groupHandle: h, groupName: name, sortOrder: sortOrder, products: groups[h] };
      });

      // Groups without a sort_order value fall back to Infinity (sorted
      // last); ties keep their original relative order since Array#sort
      // is stable.
      result.sort(function (a, b) { return a.sortOrder - b.sortOrder; });

      if (ungrouped.length) result.push({ groupHandle: null, groupName: null, sortOrder: Infinity, products: ungrouped });
      return result;
    }



    function injectCurrentIfMissing(products, swatchMap) {
      if (!CURRENT_PRODUCT_HANDLE) return Promise.resolve(products);


      var alreadyPresent = products.some(function (p) {
        return p.handle === CURRENT_PRODUCT_HANDLE;
      });


      if (alreadyPresent) return Promise.resolve(products);


      log('current product not in metaobject — injecting swatch from Liquid data:', CURRENT_PRODUCT_HANDLE);


      var syntheticProduct = {
        handle:               CURRENT_PRODUCT_HANDLE,
        onlineStoreUrl:       null,
        featuredImage:        CURRENT_PRODUCT_IMAGE
                                ? { url: CURRENT_PRODUCT_IMAGE, altText: CURRENT_PRODUCT_IMAGE_ALT }
                                : null,
        colorMetafield:       CURRENT_PRODUCT_COLOR ? { value: CURRENT_PRODUCT_COLOR } : null,
        colorGroupMetafield:  null,
        variants:             { nodes: [] }
      };


      if (CURRENT_PRODUCT_COLOR && !swatchMap[CURRENT_PRODUCT_COLOR]) {
        log('fetching swatch for injected product color:', CURRENT_PRODUCT_COLOR);
        return window.NativeShops.swatchShared.fetchSwatches(SHOP_DOMAIN, STOREFRONT_TOKEN, [CURRENT_PRODUCT_COLOR]).then(function (extraSwatches) {
          Object.keys(extraSwatches).forEach(function (k) { swatchMap[k] = extraSwatches[k]; });
          return [syntheticProduct].concat(products);
        });
      }


      return Promise.resolve([syntheticProduct].concat(products));
    }



    // Reads liveSizeDataByHandle via closure (declared further below in
    // this same initContainer scope) — see isFullyOOS()'s own comment
    // for why live data is preferred and the cached `variants` field is
    // only a first-paint fallback.
    function filterProducts(products) {
      return products.filter(function (p) {
        var productPath = normalizePath(
          p.onlineStoreUrl
            ? new URL(p.onlineStoreUrl).pathname
            : '/products/' + p.handle
        );


        if (productPath === CURRENT_PATH) {
          log('keeping current product:', p.handle);
          return true;
        }


        if (isFullyOOS(p, liveSizeDataByHandle[p.handle])) {
          log('hiding fully-OOS sibling:', p.handle);
          return false;
        }


        return true;
      });
    }



    function renderThumbnail(products, swatchMap, colorGroupMap) {
      var filtered = filterProducts(products);
      var grouped  = groupProducts(filtered, colorGroupMap);
      var selectedSizeHandlesNorm = getSelectedSizeHandlesNorm(CONTAINER);
      var html     = '';


      grouped.forEach(function (group) {
        html += '<div class="cross-links-group-block">';
        if (group.groupName) html += '<div class="cross-links-group-label">' + group.groupName + '</div>';
        html += '<div class="cross-links-group-items">';


        html += group.products.map(function (p) {
          var hrefPath     = p.onlineStoreUrl ? new URL(p.onlineStoreUrl).pathname : '/products/' + p.handle;
          var isCurrent    = normalizePath(hrefPath) === CURRENT_PATH;
          var sel          = isCurrent ? ' cl-item-selected' : '';
          var sizeUnavail  = isSizeUnavailable(liveSizeDataByHandle[p.handle], selectedSizeHandlesNorm) ? ' product-cross-link-thumbnail--size-unavailable' : '';
          var colorHandle  = p.colorMetafield && p.colorMetafield.value;
          var swatch       = (colorHandle && swatchMap && swatchMap[colorHandle]) || {};
          var swatchImgUrl = swatch.color_image && swatch.color_image.reference && swatch.color_image.reference.image && swatch.color_image.reference.image.url;
          var swatchImgAlt = (swatch.color_image && swatch.color_image.reference && swatch.color_image.reference.image && swatch.color_image.reference.image.altText) || '';
          var colorHex     = swatch.color_hex && swatch.color_hex.value;


          var visual = '';
          if (swatchImgUrl) {
            visual = '<img src="' + buildSizedUrl(swatchImgUrl, 45) + '" alt="' + swatchImgAlt + '" loading="lazy" width="30" height="30">';
          } else if (!colorHex && p.featuredImage) {
            visual = '<img src="' + buildSizedUrl(p.featuredImage.url, 45) + '" alt="' + (p.featuredImage.altText || '') + '" loading="lazy" width="30" height="30">';
          }


          var hexStyle = (!swatchImgUrl && colorHex) ? ' style="background-color:' + colorHex + ';"' : '';
          // 2026-08-25 (ported forward): mark the current color's own link
          // so the click-prevention listener above can stop a wasted
          // same-page reload.
          var currentAttr = isCurrent ? ' data-current-color-link' : '';
          return '<div class="product-cross-link-item"><a href="' + hrefPath + '"' + currentAttr + '><div class="product-cross-link-thumbnail' + sel + sizeUnavail + '"' + hexStyle + '>' + visual + '</div></a></div>';
        }).join('');


        html += '</div></div>';
      });


      CONTAINER.innerHTML = html;
      finalize();
    }



    function renderSwatches(products, swatchMap, colorGroupMap) {
      var filtered = filterProducts(products);
      var grouped  = groupProducts(filtered, colorGroupMap);
      var selectedSizeHandlesNorm = getSelectedSizeHandlesNorm(CONTAINER);
      var html     = '';


      grouped.forEach(function (group) {
        if (group.groupName) html += '<li class="cross-links-group-label">' + group.groupName + '</li>';


        html += group.products.map(function (p) {
          var hrefPath    = p.onlineStoreUrl ? new URL(p.onlineStoreUrl).pathname : '/products/' + p.handle;
          var isSelected  = normalizePath(hrefPath) === CURRENT_PATH;
          var sizeUnavail = isSizeUnavailable(liveSizeDataByHandle[p.handle], selectedSizeHandlesNorm);
          var colorHandle = p.colorMetafield && p.colorMetafield.value;
          var swatch      = (colorHandle && swatchMap[colorHandle]) || {};
          var colorName   = (swatch.color_name && swatch.color_name.value) || '';
          var colorHex    = (swatch.color_hex  && swatch.color_hex.value)  || '';
          var imgNode     = swatch.color_image && swatch.color_image.reference && swatch.color_image.reference.image;
          var colorImgUrl = (imgNode && imgNode.url)     || '';
          var colorImgAlt = (imgNode && imgNode.altText) || colorName;


          var dotStyle = '', dotContent = '';
          if (colorImgUrl) {
            dotContent = '<img src="' + buildSizedUrl(colorImgUrl, 120) + '" alt="' + colorImgAlt + '" loading="lazy" width="60" height="60">';
          } else if (colorHex) {
            dotStyle = ' style="background:' + colorHex + ';"';
          } else if (p.featuredImage) {
            dotContent = '<img src="' + buildSizedUrl(p.featuredImage.url, 120) + '" alt="' + (p.featuredImage.altText || '') + '" loading="lazy" width="60" height="60">';
          }


          // 2026-08-25 (ported forward): mark the current color's own link
          // so the click-prevention listener above can stop a wasted
          // same-page reload.
          var currentAttr = isSelected ? ' data-current-color-link' : '';
          return '<li class="swatch-list-item text-small">' +
            '<a' + (isSelected ? ' class="selected"' : '') + currentAttr + ' href="' + hrefPath + '" title="' + colorName + (sizeUnavail ? ' (not available in your selected size)' : '') + '">' +
            '<div class="swatch-dot' + (sizeUnavail ? ' swatch-dot--size-unavailable' : '') + '"' + dotStyle + '>' + dotContent + '</div>' +
            '</a></li>';
        }).join('');
      });


      CONTAINER.innerHTML = html;
      finalize();
      log('rendered', filtered.length, 'swatches (', products.length - filtered.length, 'fully-OOS siblings hidden)');
    }



    // Retained so the native:size-selection-changed listener below can
    // re-render with the already-fetched data on a same-page size click,
    // with no refetch and no cache interaction.
    var lastRenderData = null;

    // Live per-size availability, keyed by product handle — see
    // fetchLiveSizeAvailability() above. Populated once per page load,
    // BEFORE the first render (see boot below); never read from or
    // written to localStorage.
    var liveSizeDataByHandle = {};

    function renderFromData(data, swatchMap) {
      lastRenderData    = data;
      var products      = data.products      || [];
      var sm            = swatchMap || data.swatchMap || {};
      var colorGroupMap = data.colorGroupMap || {};


      if (LINK_TYPE === 'thumbnail') {
        renderThumbnail(products, sm, colorGroupMap);
      } else {
        renderSwatches(products, sm, colorGroupMap);
      }
    }



    // 2026-08-19: native-size-selector.js dispatches this (bubbled to
    // document) from renderSelection() any time the shopper's selected
    // size changes — including deselecting. Re-render from the data
    // already in hand so the per-size-per-color flag (isSizeUnavailable())
    // updates immediately without a network round-trip. Availability
    // itself doesn't change just because a size was clicked, so this
    // reuses liveSizeDataByHandle rather than refetching it.
    document.addEventListener('native:size-selection-changed', function () {
      if (!lastRenderData) return;
      renderFromData(lastRenderData, lastRenderData.swatchMap);
    });



    // ─── Boot: identity (cache-or-fetch, handled entirely inside
    // assets/native-swatch-shared.js) + this file's own group-metadata
    // fetch, combined into one render. Sorting alphabetically by path
    // preserves the original within-group ordering behavior — the
    // shared module's identity fetch doesn't sort (collection grid never
    // needed it to), so this file does it once, right after resolving
    // identity, same as before. ─────────────────────────────────────
    window.NativeShops.swatchShared.getStyleIdentity(SHOP_DOMAIN, STOREFRONT_TOKEN, STYLE_HANDLE).then(function (identity) {
      if (!identity && !CURRENT_PRODUCT_HANDLE) {
        CONTAINER.innerHTML = '<li style="color:orange;font-size:11px;">[cross-links] 0 products — check style_colors on metaobject "' + STYLE_HANDLE + '"</li>';
        finalize();
        return;
      }

      var products  = (identity && identity.products) || [];
      var swatchMap = (identity && identity.swatchMap) || {};

      products.sort(function (a, b) {
        var pa = normalizePath(a.onlineStoreUrl ? new URL(a.onlineStoreUrl).pathname : '/products/' + a.handle);
        var pb = normalizePath(b.onlineStoreUrl ? new URL(b.onlineStoreUrl).pathname : '/products/' + b.handle);
        return pa.localeCompare(pb);
      });

      var seenGroups = {}, groupHandles = [];
      products.forEach(function (p) {
        var g = p.colorGroupMetafield && p.colorGroupMetafield.value;
        if (g && !seenGroups[g]) { seenGroups[g] = true; groupHandles.push(g); }
      });

      // 2026-09-02: fetchColorGroups() and fetchLiveSizeAvailability() have
      // no data dependency on each other — both only need the sibling
      // handle list, which injectCurrentIfMissing() already has before it
      // even resolves. The old code nested them (Promise.all for the first
      // pair, THEN fetchLiveSizeAvailability afterward), so every PDP load
      // paid for two sequential network round trips before any swatch
      // could paint, even on a fully warm identity cache. Fixed by starting
      // fetchColorGroups immediately alongside injectCurrentIfMissing (as
      // before), and starting fetchLiveSizeAvailability as soon as
      // injectCurrentIfMissing resolves — not waiting on fetchColorGroups
      // too. Render still waits for both to finish (same final gating
      // behavior, same "never flash" guarantee from v2.3.0), it's just the
      // two independent fetches now genuinely overlap instead of one
      // blocking the other's start. No change to what's fetched or cached
      // — cache prefix intentionally left alone.
      var injectPromise = injectCurrentIfMissing(products, swatchMap);
      var colorGroupsPromise = fetchColorGroups(groupHandles);

      injectPromise.then(function (finalProducts) {
        var handles = finalProducts.map(function (p) { return p.handle; }).filter(Boolean);
        return Promise.all([colorGroupsPromise, fetchLiveSizeAvailability(handles)]).then(function (results) {
          var colorGroupMap = results[0];
          var map = results[1];
          var data = { products: finalProducts, swatchMap: swatchMap, colorGroupMap: colorGroupMap };
          liveSizeDataByHandle = map;
          renderFromData(data);
        });
      });
    }).catch(function (e) {
      err('error:', e);
      CONTAINER.innerHTML = '<li style="color:red;font-size:11px;">[cross-links] error: ' + e.message + '</li>';
      finalize();
    });
  }



  function boot() {
    var nodes = document.querySelectorAll('[data-native-cross-links]:not([data-native-cross-links-ready])');
    Array.prototype.forEach.call(nodes, function (node) {
      node.setAttribute('data-native-cross-links-ready', '1');
      initContainer(node);
    });
  }



  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }



  document.addEventListener('shopify:section:load', boot);
})();
